import React from 'react';
import clsx from 'clsx';
import { EyeSlashIcon } from '@heroicons/react/24/outline';

import { Spinner } from '@oysterjs/ui/spinner';
import { getStaticsBaseUrl } from '@oysterjs/core/config';

import type { HtmlDocumentTemplateFieldsFragment } from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { useDebouncedValue } from '~/hooks/use-list-controls';
import { HtmlDocumentEditor as PagedEditor } from '~/components/document-editor';
import { Editor as LegacyEditor } from '~/pages/copilot/workspace/legacy-editor/html-document-editor';
import {
  type SaveStatus,
  SaveStatusIndicator
} from '~/components/document-editor/artifact-focus-html-document-save-status-indicator';

/**
 * Right-hand preview pane for the HTML editor.
 *
 * - Create mode (no template yet): nothing to preview until first save.
 * - Edit mode, style-only template: PDF iframe preview.
 * - Edit mode, template (has bodyTemplate): toggles between PDF iframe and
 *   the inline TipTap body editor. With designs on, a saved template is edited in the paged
 *   editor; a template with no id yet, or any template with designs off, keeps main's editor.
 */
export function HtmlEditorPreviewPane(props: {
  editMode: boolean;
  template?: HtmlDocumentTemplateFieldsFragment;
  /** True when the template has a body to edit (i.e. it's a template, not a style). */
  isTemplate: boolean;
  /** False when the caller may read the template but not change it. */
  editable: boolean;
  renderingPreview: boolean;
  renderPreviewVersion: number;
  /** The design chosen in the form, which the preview shows before it is saved. */
  designId?: string | null;
  /** Editor callbacks — only used for template-mode (with body). */
  onEditorContentChange?: (content: string) => Promise<void> | void;
  /** Called after the body editor persists so the preview iframe can reload. */
  onEditorContentSaved?: () => void;
}) {
  const designsOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  const [showEditorPreview, setShowEditorPreview] = React.useState(true);
  const [editorSaveStatus, setEditorSaveStatus] = React.useState<SaveStatus>('idle');
  const [bodyTemplate, setBodyTemplate] = React.useState(() => props.template?.bodyTemplate || '');
  // Arrowing through a closed select changes the design once per key press.
  const settledDesignId = useDebouncedValue(props.designId, 400);

  // Reset only when opening a different template — not when loader data refreshes
  // after an inline save (that would overwrite local bodyTemplate with stale props).
  React.useEffect(() => {
    setBodyTemplate(props.template?.bodyTemplate || '');
  }, [props.template?.id]);

  if (!props.editMode || !props.template) {
    // Create flow: no preview is possible until first save.
    if (props.renderingPreview) {
      return (
        <div className="text-center w-full px-16 py-32 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-700 sticky top-0">
          <Spinner size="size-12" color="neutral" />
          <h3 className="mt-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
            Generating preview...
          </h3>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            This may take a few seconds.
          </p>
        </div>
      );
    }
    return (
      <div className="text-center w-full px-16 py-32 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-700 sticky top-0">
        <EyeSlashIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
          Preview not available
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Save this template to generate a preview.
        </p>
      </div>
    );
  }

  const template = props.template;
  const Editor = designsOn && template.id ? PagedEditor : LegacyEditor;
  // The template's stylesheet URL never changes, so what the saved template looks like has to
  // reach the editor as a revision or the canvas keeps the previous design. Body saves are left
  // out: they change nothing the stylesheet depends on.
  const stylesheetRevision = [
    template.designId ?? '',
    template.design?.archived ? 'archived' : 'live',
    template.startPages,
    template.endPages,
    template.printsOwnHeaderFooter ? 'own' : 'design'
  ].join(':');
  const unsavedDesign = (designId: string | null | undefined) =>
    designId !== undefined && (designId ?? null) !== (template.designId ?? null)
      ? designId
      : undefined;
  const previewDesignId = unsavedDesign(props.designId);
  const previewQuery = new URLSearchParams({ v: String(props.renderPreviewVersion) });
  const renderedDesignId = unsavedDesign(settledDesignId);
  if (renderedDesignId !== undefined) {
    previewQuery.set('design', renderedDesignId ?? 'default');
  }
  const previewSrc = `${getStaticsBaseUrl()}/agency/preview-html/${template.id}?${previewQuery.toString()}`;

  // Style-only: iframe preview only. Template: toggle editor vs preview without unmounting
  // the editor (unmounting re-seeded TipTap from stale loader `bodyTemplate`).
  if (!props.isTemplate) {
    return (
      <div className="sticky top-0 flex flex-col h-[calc(100vh-6rem)]">
        <PreviewFrame src={previewSrc} shown />
      </div>
    );
  }

  return (
    <div className="sticky top-0 flex flex-col h-[calc(100vh-6rem)]">
      <div className="flex-shrink-0 flex items-center justify-between h-9 mb-2">
        {showEditorPreview ? (
          <div className="flex-1" />
        ) : (
          <SaveStatusIndicator status={editorSaveStatus} className="flex items-center" />
        )}
        <button
          type="button"
          onClick={() => setShowEditorPreview((prev) => !prev)}
          className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 px-2 py-1.5 rounded-md"
        >
          {showEditorPreview ? 'Show editor' : 'Show preview'}
        </button>
      </div>

      <div
        data-testid="html-editor-body-pane"
        className={clsx(
          'relative flex-1 min-h-0 flex flex-col -mt-2',
          showEditorPreview && 'hidden'
        )}
      >
        <div className="relative flex-1 w-full flex flex-col min-h-0">
          <Editor
            content={bodyTemplate}
            editable={props.editable}
            onUpdate={async (content) => {
              if (props.onEditorContentChange) {
                await props.onEditorContentChange(content);
                setBodyTemplate(content);
                props.onEditorContentSaved?.();
              }
            }}
            onSaveStatusChange={setEditorSaveStatus}
            templateId={template.id}
            templateDesignId={previewDesignId}
            artifactRevision={stylesheetRevision}
            showPreview={false}
            onPreviewToggle={() => setShowEditorPreview(true)}
          />
        </div>
      </div>

      <div
        data-testid="html-editor-preview-iframe-pane"
        className={clsx('flex-1 min-h-0 flex flex-col', !showEditorPreview && 'hidden')}
      >
        <PreviewFrame src={previewSrc} shown={showEditorPreview} />
      </div>
    </div>
  );
}

/**
 * Each load of the preview is a full PDF render, so a hidden preview keeps the page it last
 * showed and loads the latest only once it is shown again.
 */
function PreviewFrame({ src, shown }: { src: string; shown: boolean }) {
  const [shownSrc, setShownSrc] = React.useState(src);
  if (shown && src !== shownSrc) {
    setShownSrc(src);
  }
  const [loadedSrc, setLoadedSrc] = React.useState<string | null>(null);
  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <iframe
        src={shownSrc}
        onLoad={() => setLoadedSrc(shownSrc)}
        className="flex-1 min-h-0 w-full rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-700"
        title="Style preview"
      />
      {loadedSrc !== shownSrc && (
        <div
          role="status"
          className="absolute right-3 top-3 flex items-center gap-2 rounded-md bg-white/90 px-2 py-1 text-xs text-neutral-600 shadow-sm dark:bg-neutral-800/90 dark:text-neutral-300"
        >
          <Spinner size="size-4" color="neutral" />
          Updating preview…
        </div>
      )}
    </div>
  );
}
